import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { OfferDetailView, OfferStatus, OfferTermsRequest, RecordAnswerRequest } from '@nest-recruit/shared';
import { errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { OFFER_APPROVALS_KEY, OFFERS_KEY, offerKey, offersApi } from '../api/offers-api';

export type OfferAction = 'save' | 'submit' | 'send' | 'negotiate' | 'revision' | 'answer' | 'withdraw' | 'noShow' | 'approve' | 'reject' | 'upload' | 'copy';

export interface OfferActions {
  /** Which action is on its way — the feedback stays on that control (§5a rule 4). */
  readonly pending: OfferAction | null;
  readonly save: (terms: OfferTermsRequest) => Promise<boolean>;
  readonly submit: () => void;
  readonly send: () => void;
  readonly negotiate: () => void;
  readonly sendRevision: () => void;
  readonly answer: (input: RecordAnswerRequest) => void;
  readonly withdraw: (justification: string) => void;
  readonly noShow: () => void;
  readonly approve: (stepId: string, version: number) => void;
  readonly reject: (stepId: string, version: number, reason: string) => void;
  readonly upload: (file: File) => void;
  readonly copyMessage: () => void;
}

/**
 * Every OFR-01/03 change, act first and confirm after (CLAUDE.md §5a rule 1): the status on
 * screen changes at once, the server's answer — the whole offer — replaces it, and a refusal
 * rolls it back with the reason. The provisional state holds until that answer lands (rule 3).
 */
export function useOfferActions(offer: OfferDetailView): OfferActions {
  const queryClient = useQueryClient();
  const toasts = useToasts();
  const [pending, setPending] = useState<OfferAction | null>(null);
  const key = offerKey(offer.id);

  async function run(action: OfferAction, status: OfferStatus | null, call: () => Promise<OfferDetailView>, failure: string, success: string | null, detail?: string): Promise<boolean> {
    const before = queryClient.getQueryData<OfferDetailView>(key);
    setPending(action);
    if (before && status) queryClient.setQueryData<OfferDetailView>(key, { ...before, status });
    try {
      queryClient.setQueryData(key, await call());
      if (success) toasts.show({ tone: 'success', message: success, ...(detail ? { detail } : {}) });
      // The job's Offers tab, the comparison and the approvers' lists read this too.
      void queryClient.invalidateQueries({ queryKey: OFFERS_KEY, predicate: (query) => query.queryKey[1] !== 'detail' });
      void queryClient.invalidateQueries({ queryKey: OFFER_APPROVALS_KEY });
      return true;
    } catch (error) {
      if (before) queryClient.setQueryData(key, before);
      toasts.show({ tone: 'danger', message: failure, detail: errorMessage(error) });
      return false;
    } finally {
      setPending(null);
    }
  }

  const name = offer.candidateName;
  return {
    pending,
    save: (terms) => run('save', null, () => offersApi.saveTerms(offer.id, terms), "The offer wasn't saved", null),
    submit: () => void run('submit', 'in_approval', () => offersApi.submit(offer.id, offer.version), "It didn't go for approval", 'Sent for approval', 'The first approver has been told.'),
    send: () => void run('send', 'sent', () => offersApi.send(offer.id, offer.version), "The offer wasn't sent", `Offer sent to ${name}`, 'They get an email with the link to accept or decline.'),
    negotiate: () => void run('negotiate', 'negotiating', () => offersApi.negotiate(offer.id, offer.version), "The renegotiation didn't start", 'Renegotiating', `Change the figures, then send the revised offer. ${name}'s link says a revised offer is coming.`),
    sendRevision: () => void run('revision', null, () => offersApi.sendRevision(offer.id, offer.version), "The revised offer didn't go", 'Revised offer on its way', 'Inside the band it goes at once; outside, it goes for approval first.'),
    answer: (input) =>
      void run('answer', input.answer, () => offersApi.answer(offer.id, input), "The answer wasn't recorded", input.answer === 'accepted' ? `${name} accepted` : `${name} declined`, 'Recorded from the call.'),
    withdraw: (justification) => void run('withdraw', 'withdrawn_by_company', () => offersApi.withdraw(offer.id, { version: offer.version, justification }), "The offer wasn't withdrawn", 'Offer withdrawn', 'Tell the candidate yourself — no email goes from the system.'),
    noShow: () => void run('noShow', 'accepted_no_show', () => offersApi.noShow(offer.id, offer.version), "That wasn't recorded", `${name} marked as not joined`, 'The recruiter, hiring manager and HR are told.'),
    approve: (stepId, version) => void run('approve', null, () => offersApi.decide(stepId, { version, decision: 'approve' }), "The approval wasn't recorded", 'Approved'),
    reject: (stepId, version, reason) => void run('reject', 'rejected', () => offersApi.decide(stepId, { version, decision: 'reject', reason }), "That wasn't recorded", 'Sent back to the recruiter', 'They are told your reason.'),
    upload: (file) => void run('upload', null, () => offersApi.uploadSignedCopy(offer.id, file), "The signed copy wasn't added", 'Signed copy added'),
    copyMessage: () => {
      setPending('copy');
      offersApi
        .copyMessage(offer.id)
        .then(async ({ text }) => {
          await navigator.clipboard.writeText(text);
          toasts.show({ tone: 'success', message: 'Message copied', detail: 'Paste it into WhatsApp.' });
        })
        .catch((error: unknown) => toasts.show({ tone: 'danger', message: "The message wasn't copied", detail: errorMessage(error) }))
        .finally(() => setPending(null));
    },
  };
}
